import { Controller } from "@hotwired/stimulus"

// Drives the panel next to the admin workspace rail: which workspace's screens
// it lists.
//
// The panel is always open. Hovering a workspace in the rail previews its
// screens without leaving the page; the panel returns to the workspace you are
// actually in once the pointer leaves.
export default class extends Controller {
  static targets = ["panel", "section"]
  static values = { activeSection: String }

  // Long enough to cover the gap between two rail items, short enough that
  // leaving the sidebar still feels immediate.
  static PREVIEW_GRACE = 100

  // A workspace only takes the panel once the pointer settles on it. Long
  // enough that one brushed on the way past the rail is ignored, short enough
  // that stopping on a workspace still shows its screens right away.
  static PREVIEW_INTENT = 150

  connect() {
    // Workspaces without screens of their own (Home, Search) render no panel.
    if (!this.hasPanelTarget) return

    this.previewedSection = null

    this.renderWithoutTransition()
  }

  disconnect() {
    clearTimeout(this.endTimeout)
    clearTimeout(this.intentTimeout)
  }

  preview(event) {
    if (!this.hasPanelTarget) return

    const section = String(event.params.section)

    clearTimeout(this.endTimeout)
    clearTimeout(this.intentTimeout)

    // A workspace with no screens of its own leaves the panel as it is rather
    // than emptying it under the pointer.
    if (!this.sectionIds.includes(section)) return
    if (this.previewedSection === section) return

    // Reaching a workspace by keyboard is already deliberate; only the pointer
    // has to hold still first.
    if (event.type === "focus") return this.showPreview(section)

    this.intentTimeout = setTimeout(() => this.showPreview(section), this.constructor.PREVIEW_INTENT)
  }

  // The pointer moved on before the workspace under it had earned the panel.
  cancelPreview() {
    clearTimeout(this.intentTimeout)
  }

  showPreview(section) {
    this.previewedSection = section
    this.render()
  }

  endPreview() {
    clearTimeout(this.intentTimeout)

    if (!this.hasPanelTarget || this.previewedSection === null) return

    clearTimeout(this.endTimeout)
    this.endTimeout = setTimeout(() => {
      // Someone who tabbed into the previewed screens keeps them: hiding a
      // section holding the focus would drop the caret on the floor.
      if (this.previewHasFocus) return

      this.previewedSection = null
      this.render()
    }, this.constructor.PREVIEW_GRACE)
  }

  render() {
    const visible = this.visibleSection

    this.sectionTargets.forEach((section) => {
      const shown = section.dataset.adminSidebarSection === visible

      section.classList.toggle("opacity-0", !shown)
      section.classList.toggle("translate-x-1", !shown)
      section.inert = !shown
      section.setAttribute("aria-hidden", String(!shown))
    })
  }

  // The sections are rendered in place, so swapping them on the first render
  // would otherwise animate on every page load.
  renderWithoutTransition() {
    const animated = this.sectionTargets

    animated.forEach((element) => { element.style.transitionDuration = "0s" })
    this.render()
    requestAnimationFrame(() => {
      animated.forEach((element) => { element.style.transitionDuration = "" })
    })
  }

  // What the panel should be showing: the preview under the pointer, otherwise
  // the workspace being used.
  get visibleSection() {
    if (this.previewedSection) return this.previewedSection

    return this.activeSectionValue || null
  }

  get previewHasFocus() {
    const previewed = this.sectionTargets.find(
      (section) => section.dataset.adminSidebarSection === this.previewedSection
    )

    return Boolean(previewed?.contains(document.activeElement))
  }

  get sectionIds() {
    return this.sectionTargets.map((section) => section.dataset.adminSidebarSection)
  }
}
